import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Copy, Check } from 'lucide-react';
import { useState } from 'react';

const AuthErrorPage = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const replotDomain = window.location.hostname;
  const firebaseInstructions = `https://console.firebase.google.com/project/_/authentication/settings`;

  const copyDomain = () => {
    navigator.clipboard.writeText(replotDomain);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="bg-red-100 rounded-full p-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
        </div>

        {/* Title & Description */}
        <h1 className="text-2xl font-bold text-grey-900 text-center mb-2">
          Authentication Issue
        </h1>
        <p className="text-grey-600 text-center mb-6">
          We need to configure your Firebase settings to enable Google sign-in.
        </p>

        {/* Steps */}
        <div className="space-y-6 mb-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h2 className="font-semibold text-grey-900 mb-3 flex items-center gap-2">
              <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
              Copy your domain
            </h2>
            <p className="text-sm text-grey-600 mb-3">
              This is your app's unique Replit domain:
            </p>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={replotDomain}
                readOnly
                className="flex-1 px-3 py-2 border border-grey-300 rounded bg-grey-50 text-sm font-mono"
              />
              <button
                onClick={copyDomain}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center gap-2 transition"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h2 className="font-semibold text-grey-900 mb-3 flex items-center gap-2">
              <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
              Add to Firebase Console
            </h2>
            <ol className="text-sm text-grey-600 space-y-2 list-decimal list-inside">
              <li>Go to <a href={firebaseInstructions} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Firebase Console Authentication Settings</a></li>
              <li>Scroll to "Authorized domains"</li>
              <li>Click "Add domain"</li>
              <li>Paste your domain from above</li>
              <li>Click "Add"</li>
            </ol>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h2 className="font-semibold text-grey-900 mb-3 flex items-center gap-2">
              <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
              Retry signing in
            </h2>
            <p className="text-sm text-grey-600">
              After adding your domain, return and try signing in again.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 px-4 py-2 border border-grey-300 rounded-lg text-grey-700 hover:bg-grey-50 transition font-medium"
          >
            Go Back
          </button>
          <button
            onClick={() => window.location.href = firebaseInstructions}
            target="_blank"
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition font-medium"
          >
            Firebase Console
          </button>
        </div>

        {/* Help Text */}
        <p className="text-xs text-grey-500 text-center mt-6">
          Need help? Contact <a href="mailto:support@pebbletheapp.com" className="text-blue-600 hover:underline">support@pebbletheapp.com</a>
        </p>
      </div>
    </div>
  );
};

export default AuthErrorPage;
